import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, Eye, Ban, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchClients();
  }, [page, search, statusFilter]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/users", {
        params: { page, search, status: statusFilter, per_page: 20 },
      });
      setClients(res.data.users);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (user) => {
    const status = user.status || "active";
    const colors = {
      active: "bg-emerald-500/10 text-emerald-500",
      suspended: "bg-red-500/10 text-red-500",
      trial: "bg-yellow-500/10 text-yellow-500",
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || colors.active}`}>
        {status === "active" ? "Ativo" : status === "suspended" ? "Suspenso" : "Trial"}
      </span>
    );
  };

  const getWhatsAppStatus = (tenant) => {
    const status = tenant?.evolution_status || "disconnected";
    if (status === "connected") {
      return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    }
    return <Ban className="w-4 h-4 text-red-500" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Clientes</h1>
          <p className="text-zinc-400 mt-1">Gerencie todos os clientes do SaaS</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar por nome, email ou tenant ID..."
            className="w-full pl-11 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">Todos os status</option>
          <option value="active">Ativos</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspensos</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">
                  Plano
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">
                  WhatsApp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">
                  Cadastro
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-zinc-400 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-zinc-400">
                    Carregando...
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-zinc-400">
                    Nenhum cliente encontrado
                  </td>
                </tr>
              ) : (
                clients.map((user) => (
                  <tr key={user.id} className="hover:bg-zinc-800/30">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-white">{user.name}</div>
                        <div className="text-xs text-zinc-400">{user.tenant?.name || "-"}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-zinc-400 text-sm">{user.email}</td>
                    <td className="px-6 py-4">{getStatusBadge(user)}</td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-zinc-400 capitalize">
                        {user.plan_id || "Nenhum"}
                      </span>
                    </td>
                    <td className="px-6 py-4">{getWhatsAppStatus(user.tenant)}</td>
                    <td className="px-6 py-4 text-zinc-400 text-sm">
                      {new Date(user.created_at).toLocaleDateString("pt-BR")}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/admin/clients/${user.id}`}
                        className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400"
                      >
                        <Eye size={18} />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white"
            >
              Anterior
            </button>
            <span className="text-sm text-zinc-400">
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white"
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
